import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { speakText } from '../utils/audioUtils';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import { fetchWordItemsByCategory } from '../api/wordsApi';
import { supabase } from '../supabase';
import '../screens/WordsScreen.css';

interface WordItem {
  id: string;
  bengali: string;
  italian: string;
  icon: string;
  color: string;
}

// Varied colors for word items when not set from API
const WORD_ITEM_COLOR_PALETTE = [
  '#ffe5e5', '#fff9e5', '#fff0e5', '#f5e5ff', '#ffe5cc', '#fff5e5', '#f5ffe5', '#ffe5f5',
  '#ffe5f0', '#e5f0ff', '#fff0f5', '#f5f5e5', '#e5ffe5', '#f0e5ff', '#f5f0e5', '#e5f5ff',
  '#f0ffe5', '#f5e5f5', '#e5e5f5', '#ffe5d4', '#e8f5e9', '#fff3e0', '#e1f5fe', '#f3e5f5',
  '#e0f2f1', '#fce4ec', '#e8eaf6', '#fff8e1', '#f1f8e9', '#e0f7fa', '#ede7f6', '#fff9c4',
];

const WordsDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const { currentLanguage } = useLanguage();
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [fetchedItems, setFetchedItems] = useState<WordItem[] | null>(null);
  const [fetchedCategoryTitle, setFetchedCategoryTitle] = useState<{ it: string; bn: string; en: string } | null>(null);

  useEffect(() => {
    if (!type || type === 'abc' || type === 'numbers') return;
    let cancelled = false;
    fetchWordItemsByCategory(type).then((data) => {
      if (!cancelled && data && data.length > 0) {
        setFetchedItems(data.map((d, i) => ({
          id: d.id,
          italian: d.italian,
          bengali: d.bengali,
          icon: d.icon || '📦',
          color: (d.color && d.color.trim() && d.color.toLowerCase() !== '#ffe5e5') ? d.color : WORD_ITEM_COLOR_PALETTE[i % WORD_ITEM_COLOR_PALETTE.length],
        })));
      } else {
        setFetchedItems(null);
      }
    });
    return () => { cancelled = true; };
  }, [type]);

  useEffect(() => {
    if (!type) return;
    const key = type as keyof typeof content.it;
    const hasContent = content.it[key] !== undefined;
    if (hasContent) {
      setFetchedCategoryTitle(null);
      return;
    }
    let cancelled = false;
    supabase.from('word_categories').select('title_italian, title_bengali, title_english').eq('slug', type).single().then(({ data }) => {
      if (!cancelled && data) {
        const d = data as Record<string, string>;
        setFetchedCategoryTitle({ it: d.title_italian ?? '', bn: d.title_bengali ?? '', en: d.title_english ?? '' });
      }
      else setFetchedCategoryTitle(null);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Language content
  const content = {
    bn: {
      abc: "বর্ণমালা",
      numbers: "সংখ্যা",
      fruits: "ফল",
      animals: "প্রাণী",
      colors: "রঙ",
      body: "শরীরের অংশ",
      food: "খাবার",
      family: "পরিবার",
      clothes: "পোশাক",
      vehicles: "যানবাহন",
      weather: "আবহাওয়া",
      greetings: "অভিবাদন",
      commonWords: "শব্দ",
      advancedWords: "আয়ত্ত",
      actions: "ক্রিয়া",
      time: "সময়",
      places: "স্থান",
      school: "স্কুল",
      emotions: "আবেগ",
      sports: "খেলা",
      suffixAzione: "-azione",
      suffixZione: "-zione",
      suffixMente: "-mente",
      suffixIta: "-ità",
      suffixOreAtore: "পেশা",
      suffixOso: "-oso",
      suffixIssimo: "-issimo",
      verbsInfinitive: "ক্রিয়া",
      prefixRi: "ri-",
      back: "ফিরে যান",
      tapToSpeak: "শোনার জন্য আলতো চাপুন"
    },
    it: {
      abc: "Alfabeto",
      numbers: "Numeri",
      fruits: "Frutta",
      animals: "Animali",
      colors: "Colori",
      body: "Parti del Corpo",
      food: "Cibo",
      family: "Famiglia",
      clothes: "Vestiti",
      vehicles: "Veicoli",
      weather: "Tempo",
      greetings: "Saluti",
      commonWords: "Parole Comuni",
      advancedWords: "Parole Avanzate",
      actions: "Azioni",
      time: "Tempo",
      places: "Luoghi",
      school: "Scuola",
      emotions: "Emozioni",
      sports: "Sport",
      suffixAzione: "-azione",
      suffixZione: "-zione",
      suffixMente: "-mente",
      suffixIta: "-ità",
      suffixOreAtore: "Lavori",
      suffixOso: "-oso",
      suffixIssimo: "-issimo",
      verbsInfinitive: "Verbi",
      prefixRi: "ri-",
      back: "Indietro",
      tapToSpeak: "Tocca per sentire"
    },
    en: {
      abc: "Alphabet",
      numbers: "Numbers",
      fruits: "Fruits",
      animals: "Animals",
      colors: "Colors",
      body: "Body Parts",
      food: "Food",
      family: "Family",
      clothes: "Clothes",
      vehicles: "Vehicles",
      weather: "Weather",
      greetings: "Greetings",
      commonWords: "Common Words",
      advancedWords: "Advanced Words",
      actions: "Actions",
      time: "Time",
      places: "Places",
      school: "School",
      emotions: "Emotions",
      sports: "Sports",
      suffixAzione: "-azione",
      suffixZione: "-zione",
      suffixMente: "-mente",
      suffixIta: "-ità",
      suffixOreAtore: "Jobs",
      suffixOso: "-oso",
      suffixIssimo: "-issimo",
      verbsInfinitive: "Verbs",
      prefixRi: "ri-",
      back: "Back",
      tapToSpeak: "Tap to hear"
    }
  };

  // Color palette for letters and numbers (soft pastel colors)
  const colorPalette = [
    '#ffe5e5', '#fff9e5', '#fff0e5', '#f5e5ff', '#ffe5cc',
    '#fff5e5', '#f5ffe5', '#ffe5f5', '#ffe5f0', '#e5f0ff',
    '#fff0f5', '#f5f5e5', '#e5ffe5', '#f0e5ff', '#f5f0e5',
    '#e5f5ff', '#f0ffe5', '#f5e5f5', '#e5e5f5', '#ffe5d4',
    '#e8f5e9', '#fff3e0', '#e1f5fe', '#f3e5f5', '#e0f2f1',
    '#fce4ec', '#e8eaf6', '#fff8e1', '#f1f8e9', '#e0f7fa',
    '#ede7f6', '#fff9c4', '#e3f2fd', '#fce4ec', '#e8f5e9',
    '#fff3e0', '#e1f5fe', '#f3e5f5', '#e0f2f1', '#fce4ec',
    '#e8eaf6', '#fff8e1', '#f1f8e9', '#e0f7fa', '#ede7f6'
  ];

  // Italian alphabet letters (A-Z) with colors
  const alphabetLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map((letter, index) => ({
    letter,
    color: colorPalette[index % colorPalette.length]
  }));

  // Italian numbers 0-100 with colors
  const numbers: { number: string; color: string }[] = [];
  for (let i = 0; i <= 100; i++) {
    numbers.push({
      number: i.toString(),
      color: colorPalette[i % colorPalette.length]
    });
  }

  // Fruits with Bengali and Italian names
  const fruits: WordItem[] = [
    { id: '1', bengali: 'আপেল', italian: 'Mela', icon: '🍎', color: '#ffe5e5' },
    { id: '2', bengali: 'কলা', italian: 'Banana', icon: '🍌', color: '#fff9e5' },
    { id: '3', bengali: 'কমলা', italian: 'Arancia', icon: '🍊', color: '#fff0e5' },
    { id: '4', bengali: 'আঙুর', italian: 'Uva', icon: '🍇', color: '#f5e5ff' },
    { id: '5', bengali: 'আম', italian: 'Mango', icon: '🥭', color: '#ffe5cc' },
    { id: '6', bengali: 'পেঁপে', italian: 'Papaya', icon: '🍈', color: '#fff5e5' },
    { id: '7', bengali: 'আনারস', italian: 'Ananas', icon: '🍍', color: '#f5ffe5' },
    { id: '8', bengali: 'তরমুজ', italian: 'Anguria', icon: '🍉', color: '#ffe5f5' },
    { id: '9', bengali: 'স্ট্রবেরি', italian: 'Fragola', icon: '🍓', color: '#ffe5f0' },
    { id: '10', bengali: 'আমলকি', italian: 'Amla', icon: '🫐', color: '#e5f0ff' },
    { id: '11', bengali: 'লিচু', italian: 'Litchi', icon: '◉', color: '#fff0f5' },
    { id: '12', bengali: 'নারকেল', italian: 'Cocco', icon: '🥥', color: '#f5f5e5' },
    { id: '13', bengali: 'পেয়ারা', italian: 'Guava', icon: '🍐', color: '#e5ffe5' },
    { id: '14', bengali: 'জাম', italian: 'Jamun', icon: '🫐', color: '#f0e5ff' },
    { id: '15', bengali: 'কুল', italian: 'Ber', icon: '🫐', color: '#ffe5e5' },
    { id: '16', bengali: 'খেজুর', italian: 'Dattero', icon: '🫒', color: '#f5f0e5' },
    { id: '17', bengali: 'শসা', italian: 'Cetriolo', icon: '🥒', color: '#e5ffe5' },
    { id: '18', bengali: 'টমেটো', italian: 'Pomodoro', icon: '🍅', color: '#ffe5e5' },
    { id: '19', bengali: 'পেঁয়াজ', italian: 'Cipolla', icon: '🧅', color: '#fff5e5' },
    { id: '20', bengali: 'রসুন', italian: 'Aglio', icon: '🧄', color: '#f5ffe5' }
  ];

  // Animals with Bengali and Italian names
  const animals: WordItem[] = [
    { id: '1', bengali: 'বিড়াল', italian: 'Gatto', icon: '🐱', color: '#ffe5f0' },
    { id: '2', bengali: 'কুকুর', italian: 'Cane', icon: '🐶', color: '#fff5e5' },
    { id: '3', bengali: 'গরু', italian: 'Mucca', icon: '🐄', color: '#f5f5f5' },
    { id: '4', bengali: 'ছাগল', italian: 'Capra', icon: '🐐', color: '#f5ffe5' },
    { id: '5', bengali: 'ভেড়া', italian: 'Pecora', icon: '🐑', color: '#fafafa' },
    { id: '6', bengali: 'ঘোড়া', italian: 'Cavallo', icon: '🐴', color: '#ffe5e5' },
    { id: '7', bengali: 'শূকর', italian: 'Maiale', icon: '🐷', color: '#ffe5f0' },
    { id: '8', bengali: 'হাঁস', italian: 'Anatra', icon: '🦆', color: '#e5f5ff' },
    { id: '9', bengali: 'মুরগি', italian: 'Gallina', icon: '🐔', color: '#fff5e5' },
    { id: '10', bengali: 'হাতি', italian: 'Elefante', icon: '🐘', color: '#e5e5f5' },
    { id: '11', bengali: 'সিংহ', italian: 'Leone', icon: '🦁', color: '#ffe5cc' },
    { id: '12', bengali: 'বাঘ', italian: 'Tigre', icon: '🐅', color: '#ffe5d4' },
    { id: '13', bengali: 'মহিষ', italian: 'Bufalo', icon: '🐃', color: '#f5f5f5' },
    { id: '14', bengali: 'খরগোশ', italian: 'Coniglio', icon: '🐰', color: '#fff5f5' },
    { id: '15', bengali: 'ইঁদুর', italian: 'Topo', icon: '🐭', color: '#f5f0e5' },
    { id: '16', bengali: 'পাখি', italian: 'Uccello', icon: '🐦', color: '#e5f0ff' },
    { id: '17', bengali: 'মাছ', italian: 'Pesce', icon: '🐟', color: '#e5f5ff' },
    { id: '18', bengali: 'কচ্ছপ', italian: 'Tartaruga', icon: '🐢', color: '#e5ffe5' },
    { id: '19', bengali: 'সাপ', italian: 'Serpente', icon: '🐍', color: '#f0ffe5' },
    { id: '20', bengali: 'মাকড়সা', italian: 'Ragno', icon: '🕷️', color: '#f5e5f5' }
  ];

  // Colors with Bengali and Italian names
  const colors: WordItem[] = [
    { id: '1', bengali: 'লাল', italian: 'Rosso', icon: '🔴', color: '#ffe5e5' },
    { id: '2', bengali: 'নীল', italian: 'Blu', icon: '🔵', color: '#e5f0ff' },
    { id: '3', bengali: 'সবুজ', italian: 'Verde', icon: '🟢', color: '#e5ffe5' },
    { id: '4', bengali: 'হলুদ', italian: 'Giallo', icon: '🟡', color: '#fff9e5' },
    { id: '5', bengali: 'কমলা', italian: 'Arancione', icon: '🟠', color: '#fff0e5' },
    { id: '6', bengali: 'বেগুনি', italian: 'Viola', icon: '🟣', color: '#f5e5ff' },
    { id: '7', bengali: 'গোলাপি', italian: 'Rosa', icon: '🩷', color: '#ffe5f0' },
    { id: '8', bengali: 'কালো', italian: 'Nero', icon: '⚫', color: '#e5e5e5' },
    { id: '9', bengali: 'সাদা', italian: 'Bianco', icon: '⚪', color: '#fafafa' },
    { id: '10', bengali: 'বাদামী', italian: 'Marrone', icon: '🟤', color: '#f5f0e5' },
    { id: '11', bengali: 'ধূসর', italian: 'Grigio', icon: '🔘', color: '#f5f5f5' },
    { id: '12', bengali: 'সোনালী', italian: 'Oro', icon: '🟡', color: '#fff9e5' }
  ];

  // Body parts with Bengali and Italian names
  const body: WordItem[] = [
    { id: '1', bengali: 'মাথা', italian: 'Testa', icon: '👤', color: '#ffe5f0' },
    { id: '2', bengali: 'চোখ', italian: 'Occhio', icon: '👁️', color: '#e5f0ff' },
    { id: '3', bengali: 'কান', italian: 'Orecchio', icon: '👂', color: '#fff5e5' },
    { id: '4', bengali: 'নাক', italian: 'Naso', icon: '👃', color: '#f5ffe5' },
    { id: '5', bengali: 'মুখ', italian: 'Bocca', icon: '👄', color: '#ffe5e5' },
    { id: '6', bengali: 'হাত', italian: 'Mano', icon: '✋', color: '#fff9e5' },
    { id: '7', bengali: 'পা', italian: 'Piede', icon: '🦶', color: '#ffe5f0' },
    { id: '8', bengali: 'কনুই', italian: 'Gomito', icon: '💪', color: '#e5f5ff' },
    { id: '9', bengali: 'হাঁটু', italian: 'Ginocchio', icon: '🦵', color: '#fff5e5' },
    { id: '10', bengali: 'দাঁত', italian: 'Dente', icon: '🦷', color: '#f5ffe5' },
    { id: '11', bengali: 'জিহ্বা', italian: 'Lingua', icon: '👅', color: '#ffe5f5' },
    { id: '12', bengali: 'গলা', italian: 'Gola', icon: '👔', color: '#e5e5f5' },
    { id: '13', bengali: 'বুক', italian: 'Petto', icon: '💙', color: '#ffe5e5' },
    { id: '14', bengali: 'পেট', italian: 'Pancia', icon: '🤰', color: '#fff5e5' },
    { id: '15', bengali: 'পিঠ', italian: 'Schiena', icon: '🏃', color: '#f5ffe5' },
    { id: '16', bengali: 'আঙুল', italian: 'Dito', icon: '👆', color: '#ffe5f0' }
  ];

  // Food with Bengali and Italian names
  const food: WordItem[] = [
    { id: '1', bengali: 'রুটি', italian: 'Pane', icon: '🍞', color: '#fff9e5' },
    { id: '2', bengali: 'ভাত', italian: 'Riso', icon: '🍚', color: '#f5f5e5' },
    { id: '3', bengali: 'মাছ', italian: 'Pesce', icon: '🐟', color: '#e5f5ff' },
    { id: '4', bengali: 'মাংস', italian: 'Carne', icon: '🍖', color: '#ffe5e5' },
    { id: '5', bengali: 'ডিম', italian: 'Uovo', icon: '🥚', color: '#fff9e5' },
    { id: '6', bengali: 'দুধ', italian: 'Latte', icon: '🥛', color: '#f5f5f5' },
    { id: '7', bengali: 'চা', italian: 'Tè', icon: '🍵', color: '#fff0e5' },
    { id: '8', bengali: 'পানি', italian: 'Acqua', icon: '💧', color: '#e5f5ff' },
    { id: '9', bengali: 'চিনি', italian: 'Zucchero', icon: '🍬', color: '#fff9e5' },
    { id: '10', bengali: 'নুন', italian: 'Sale', icon: '🧂', color: '#f5f5f5' },
    { id: '11', bengali: 'তেল', italian: 'Olio', icon: '🫒', color: '#fff5e5' },
    { id: '12', bengali: 'সবজি', italian: 'Verdura', icon: '🥬', color: '#e5ffe5' },
    { id: '13', bengali: 'চিকেন', italian: 'Pollo', icon: '🍗', color: '#ffe5e5' },
    { id: '14', bengali: 'পাস্তা', italian: 'Pasta', icon: '🍝', color: '#fff9e5' },
    { id: '15', bengali: 'পিজ্জা', italian: 'Pizza', icon: '🍕', color: '#fff0e5' },
    { id: '16', bengali: 'সুপ', italian: 'Zuppa', icon: '🍲', color: '#ffe5e5' }
  ];

  // Family with Bengali and Italian names
  const family: WordItem[] = [
    { id: '1', bengali: 'বাবা', italian: 'Papà', icon: '👨', color: '#e5f0ff' },
    { id: '2', bengali: 'মা', italian: 'Mamma', icon: '👩', color: '#ffe5f0' },
    { id: '3', bengali: 'ছেলে', italian: 'Figlio', icon: '👦', color: '#e5f5ff' },
    { id: '4', bengali: 'মেয়ে', italian: 'Figlia', icon: '👧', color: '#ffe5f0' },
    { id: '5', bengali: 'ভাই', italian: 'Fratello', icon: '👨‍🦱', color: '#e5e5f5' },
    { id: '6', bengali: 'বোন', italian: 'Sorella', icon: '👩‍🦰', color: '#ffe5e5' },
    { id: '7', bengali: 'দাদা', italian: 'Nonno', icon: '👴', color: '#f5f0e5' },
    { id: '8', bengali: 'দাদি', italian: 'Nonna', icon: '👵', color: '#fff5e5' },
    { id: '9', bengali: 'চাচা', italian: 'Zio', icon: '🧔', color: '#e5e5f5' },
    { id: '10', bengali: 'চাচি', italian: 'Zia', icon: '👩‍🦱', color: '#ffe5f0' },
    { id: '11', bengali: 'কাকা', italian: 'Zio', icon: '👨‍🦳', color: '#f5f5f5' },
    { id: '12', bengali: 'কাকিমা', italian: 'Zia', icon: '👩‍🦳', color: '#fff5f5' }
  ];

  // Clothes with Bengali and Italian names
  const clothes: WordItem[] = [
    { id: '1', bengali: 'শার্ট', italian: 'Camicia', icon: '👕', color: '#e5f0ff' },
    { id: '2', bengali: 'প্যান্ট', italian: 'Pantaloni', icon: '👖', color: '#e5e5f5' },
    { id: '3', bengali: 'জামা', italian: 'Vestito', icon: '👗', color: '#ffe5f0' },
    { id: '4', bengali: 'জুতা', italian: 'Scarpe', icon: '👟', color: '#f5f5f5' },
    { id: '5', bengali: 'মোজা', italian: 'Calzini', icon: '🧦', color: '#fff5e5' },
    { id: '6', bengali: 'টুপি', italian: 'Cappello', icon: '👒', color: '#ffe5e5' },
    { id: '7', bengali: 'গ্লাভস', italian: 'Guanti', icon: '🧤', color: '#e5ffe5' },
    { id: '8', bengali: 'স্কার্ফ', italian: 'Sciarpa', icon: '🧣', color: '#ffe5f0' },
    { id: '9', bengali: 'টাই', italian: 'Cravatta', icon: '👔', color: '#e5e5f5' },
    { id: '10', bengali: 'জ্যাকেট', italian: 'Giacca', icon: '🧥', color: '#f5f0e5' },
    { id: '11', bengali: 'ব্রা', italian: 'Reggiseno', icon: '👙', color: '#ffe5f0' },
    { id: '12', bengali: 'আন্ডারওয়্যার', italian: 'Mutande', icon: '🩲', color: '#f5f5f5' }
  ];

  // Vehicles with Bengali and Italian names
  const vehicles: WordItem[] = [
    { id: '1', bengali: 'গাড়ি', italian: 'Auto', icon: '🚗', color: '#ffe5e5' },
    { id: '2', bengali: 'বাস', italian: 'Autobus', icon: '🚌', color: '#fff9e5' },
    { id: '3', bengali: 'ট্রাক', italian: 'Camion', icon: '🚚', color: '#f5f0e5' },
    { id: '4', bengali: 'মোটরসাইকেল', italian: 'Moto', icon: '🏍️', color: '#ffe5f0' },
    { id: '5', bengali: 'সাইকেল', italian: 'Bicicletta', icon: '🚲', color: '#e5ffe5' },
    { id: '6', bengali: 'ট্রেন', italian: 'Treno', icon: '🚆', color: '#e5f5ff' },
    { id: '7', bengali: 'বিমান', italian: 'Aereo', icon: '✈️', color: '#e5f0ff' },
    { id: '8', bengali: 'নৌকা', italian: 'Barca', icon: '⛵', color: '#e5f5ff' },
    { id: '9', bengali: 'জাহাজ', italian: 'Nave', icon: '🚢', color: '#e5f0ff' },
    { id: '10', bengali: 'হেলিকপ্টার', italian: 'Elicottero', icon: '🚁', color: '#e5f5ff' },
    { id: '11', bengali: 'রিকশা', italian: 'Risciò', icon: '🛺', color: '#fff9e5' },
    { id: '12', bengali: 'ট্যাক্সি', italian: 'Taxi', icon: '🚕', color: '#fff9e5' }
  ];

  // Weather with Bengali and Italian names
  const weather: WordItem[] = [
    { id: '1', bengali: 'সূর্য', italian: 'Sole', icon: '☀️', color: '#fff9e5' },
    { id: '2', bengali: 'বৃষ্টি', italian: 'Pioggia', icon: '🌧️', color: '#e5f5ff' },
    { id: '3', bengali: 'মেঘ', italian: 'Nuvola', icon: '☁️', color: '#f5f5f5' },
    { id: '4', bengali: 'বাতাস', italian: 'Vento', icon: '💨', color: '#f5f5f5' },
    { id: '5', bengali: 'বজ্রপাত', italian: 'Fulmine', icon: '⚡', color: '#fff9e5' },
    { id: '6', bengali: 'তুষার', italian: 'Neve', icon: '❄️', color: '#f5f5ff' },
    { id: '7', bengali: 'বরফ', italian: 'Ghiaccio', icon: '🧊', color: '#e5f5ff' },
    { id: '8', bengali: 'কুয়াশা', italian: 'Nebbia', icon: '🌫️', color: '#f5f5f5' },
    { id: '9', bengali: 'রিমঝিম', italian: 'Pioggerella', icon: '🌦️', color: '#e5f5ff' },
    { id: '10', bengali: 'সূর্যাস্ত', italian: 'Tramonto', icon: '🌅', color: '#fff0e5' },
    { id: '11', bengali: 'সূর্যোদয়', italian: 'Alba', icon: '🌄', color: '#fff0e5' },
    { id: '12', bengali: 'রংধনু', italian: 'Arcobaleno', icon: '🌈', color: '#ffe5f0' }
  ];

  // Greetings with Bengali and Italian names
  const greetings: WordItem[] = [
    { id: '1', bengali: 'হ্যালো', italian: 'Ciao', icon: '👋', color: '#fff9e5' },
    { id: '2', bengali: 'শুভ সকাল', italian: 'Buongiorno', icon: '☀️', color: '#fff0e5' },
    { id: '3', bengali: 'শুভ সন্ধ্যা', italian: 'Buonasera', icon: '🌆', color: '#fff0e5' },
    { id: '4', bengali: 'শুভ রাত্রি', italian: 'Buonanotte', icon: '🌙', color: '#e5e5f5' },
    { id: '5', bengali: 'ধন্যবাদ', italian: 'Grazie', icon: '🙏', color: '#fff9e5' },
    { id: '6', bengali: 'কেমন আছেন?', italian: 'Come stai?', icon: '😊', color: '#ffe5f0' },
    { id: '7', bengali: 'আবার দেখা হবে', italian: 'Arrivederci', icon: '👋', color: '#e5f0ff' },
    { id: '8', bengali: 'দুঃখিত', italian: 'Scusa', icon: '😔', color: '#f5f5f5' },
    { id: '9', bengali: 'আমার নাম', italian: 'Mi chiamo', icon: '👤', color: '#ffe5f0' },
    { id: '10', bengali: 'আপনার নাম কি?', italian: 'Come ti chiami?', icon: '❓', color: '#e5f0ff' },
    { id: '11', bengali: 'পরে দেখা হবে', italian: 'A presto', icon: '👋', color: '#fff9e5' },
    { id: '12', bengali: 'স্বাগতম', italian: 'Benvenuto', icon: '👋', color: '#fff0e5' }
  ];

  // Common Words (শব্দ) - Everyday words
  const commonWords: WordItem[] = [
    { id: '1', bengali: 'হ্যাঁ', italian: 'Sì', icon: '✅', color: '#fff9e5' },
    { id: '2', bengali: 'না', italian: 'No', icon: '❌', color: '#ffe5e5' },
    { id: '3', bengali: 'এবং', italian: 'E', icon: '➕', color: '#e5f0ff' },
    { id: '4', bengali: 'অথবা', italian: 'O', icon: '🔀', color: '#ffe5f0' },
    { id: '5', bengali: 'কিন্তু', italian: 'Ma', icon: '🔄', color: '#f5ffe5' },
    { id: '6', bengali: 'এই', italian: 'Questo', icon: '👉', color: '#fff0e5' },
    { id: '7', bengali: 'সে', italian: 'Quello', icon: '👈', color: '#f0e5ff' },
    { id: '8', bengali: 'এখানে', italian: 'Qui', icon: '📍', color: '#e5fff5' },
    { id: '9', bengali: 'সেখানে', italian: 'Lì', icon: '📍', color: '#fff5e5' },
    { id: '10', bengali: 'এখন', italian: 'Ora', icon: '⏰', color: '#e5f5ff' },
    { id: '11', bengali: 'আজ', italian: 'Oggi', icon: '📅', color: '#fff9e5' },
    { id: '12', bengali: 'কাল', italian: 'Domani', icon: '📆', color: '#f5f5ff' },
    { id: '13', bengali: 'গতকাল', italian: 'Ieri', icon: '📆', color: '#fff5e5' },
    { id: '14', bengali: 'দয়া করে', italian: 'Per favore', icon: '🙏', color: '#ffe5f0' },
    { id: '15', bengali: 'ধন্যবাদ', italian: 'Grazie', icon: '🙏', color: '#fff9e5' },
    { id: '16', bengali: 'আপনাকে স্বাগতম', italian: 'Prego', icon: '👋', color: '#e5f0ff' },
    { id: '17', bengali: 'আমি', italian: 'Io', icon: '👤', color: '#ffe5f0' },
    { id: '18', bengali: 'তুমি', italian: 'Tu', icon: '👤', color: '#e5f0ff' },
    { id: '19', bengali: 'সে', italian: 'Lui/Lei', icon: '👥', color: '#f5ffe5' },
    { id: '20', bengali: 'আমরা', italian: 'Noi', icon: '👥', color: '#fff9e5' },
    { id: '21', bengali: 'তারা', italian: 'Loro', icon: '👥', color: '#f0e5ff' },
    { id: '22', bengali: 'কি', italian: 'Cosa', icon: '❓', color: '#e5fff5' },
    { id: '23', bengali: 'কোথায়', italian: 'Dove', icon: '📍', color: '#fff5e5' },
    { id: '24', bengali: 'কখন', italian: 'Quando', icon: '⏰', color: '#e5f5ff' },
    { id: '25', bengali: 'কেন', italian: 'Perché', icon: '❓', color: '#ffe5f0' },
    { id: '26', bengali: 'কেমন', italian: 'Come', icon: '❓', color: '#fff9e5' }
  ];

  // Advanced Words (আয়ত্ত) - Advanced vocabulary
  const advancedWords: WordItem[] = [
    { id: '1', bengali: 'সম্মান', italian: 'Rispetto', icon: '🙇', color: '#fff9e5' },
    { id: '2', bengali: 'সাহস', italian: 'Coraggio', icon: '💪', color: '#ffe5e5' },
    { id: '3', bengali: 'আশা', italian: 'Speranza', icon: '✨', color: '#e5f0ff' },
    { id: '4', bengali: 'স্বপ্ন', italian: 'Sogno', icon: '💭', color: '#ffe5f0' },
    { id: '5', bengali: 'সফলতা', italian: 'Successo', icon: '🏆', color: '#f5ffe5' },
    { id: '6', bengali: 'সৌন্দর্য', italian: 'Bellezza', icon: '💎', color: '#fff0e5' },
    { id: '7', bengali: 'জ্ঞান', italian: 'Saggezza', icon: '📚', color: '#f0e5ff' },
    { id: '8', bengali: 'সৃষ্টি', italian: 'Creatività', icon: '🎨', color: '#e5fff5' },
    { id: '9', bengali: 'শান্তি', italian: 'Pace', icon: '🕊️', color: '#fff5e5' },
    { id: '10', bengali: 'স্বাধীনতা', italian: 'Libertà', icon: '🕊️', color: '#e5f5ff' },
    { id: '11', bengali: 'দয়া', italian: 'Gentilezza', icon: '❤️', color: '#ffe5f0' },
    { id: '12', bengali: 'বিশ্বাস', italian: 'Fiducia', icon: '🤝', color: '#fff9e5' },
    { id: '13', bengali: 'সম্পদ', italian: 'Ricchezza', icon: '💰', color: '#f5f5ff' },
    { id: '14', bengali: 'অভিজ্ঞতা', italian: 'Esperienza', icon: '🎓', color: '#fff5e5' },
    { id: '15', bengali: 'দায়িত্ব', italian: 'Responsabilità', icon: '📋', color: '#e5f0ff' },
    { id: '16', bengali: 'নেতৃত্ব', italian: 'Leadership', icon: '👑', color: '#ffe5f0' },
    { id: '17', bengali: 'উদ্ভাবন', italian: 'Innovazione', icon: '💡', color: '#fff9e5' },
    { id: '18', bengali: 'সম্প্রীতি', italian: 'Armonia', icon: '🎵', color: '#e5fff5' },
    { id: '19', bengali: 'সামর্থ্য', italian: 'Capacità', icon: '💪', color: '#fff5e5' },
    { id: '20', bengali: 'আকাঙ্ক্ষা', italian: 'Ambizione', icon: '🚀', color: '#e5f5ff' }
  ];

  // Actions (ক্রিয়া) - Common verbs/actions
  const actions: WordItem[] = [
    { id: '1', bengali: 'যাওয়া', italian: 'Andare', icon: '🚶', color: '#fff9e5' },
    { id: '2', bengali: 'আসা', italian: 'Venire', icon: '👋', color: '#ffe5e5' },
    { id: '3', bengali: 'করা', italian: 'Fare', icon: '⚙️', color: '#e5f0ff' },
    { id: '4', bengali: 'থাকা', italian: 'Essere', icon: '📍', color: '#ffe5f0' },
    { id: '5', bengali: 'থাকা', italian: 'Stare', icon: '🏠', color: '#f5ffe5' },
    { id: '6', bengali: 'খাওয়া', italian: 'Mangiare', icon: '🍽️', color: '#fff0e5' },
    { id: '7', bengali: 'পান করা', italian: 'Bere', icon: '🥤', color: '#f0e5ff' },
    { id: '8', bengali: 'দেখা', italian: 'Vedere', icon: '👁️', color: '#e5fff5' },
    { id: '9', bengali: 'শোনা', italian: 'Sentire', icon: '👂', color: '#fff5e5' },
    { id: '10', bengali: 'বলা', italian: 'Dire', icon: '💬', color: '#e5f5ff' },
    { id: '11', bengali: 'পড়া', italian: 'Leggere', icon: '📖', color: '#ffe5f0' },
    { id: '12', bengali: 'লেখা', italian: 'Scrivere', icon: '✍️', color: '#fff9e5' },
    { id: '13', bengali: 'হাঁটা', italian: 'Camminare', icon: '🚶', color: '#f5f5ff' },
    { id: '14', bengali: 'দৌড়ানো', italian: 'Correre', icon: '🏃', color: '#fff5e5' },
    { id: '15', bengali: 'খেলা', italian: 'Giocare', icon: '🎮', color: '#e5f0ff' },
    { id: '16', bengali: 'কাজ করা', italian: 'Lavorare', icon: '💼', color: '#ffe5f0' },
    { id: '17', bengali: 'পড়াশোনা করা', italian: 'Studiare', icon: '📚', color: '#fff9e5' },
    { id: '18', bengali: 'ঘুমানো', italian: 'Dormire', icon: '😴', color: '#e5fff5' },
    { id: '19', bengali: 'জাগা', italian: 'Svegliarsi', icon: '⏰', color: '#fff5e5' },
    { id: '20', bengali: 'বসা', italian: 'Sedere', icon: '🪑', color: '#e5f5ff' },
    { id: '21', bengali: 'ওঠা', italian: 'Alzarsi', icon: '⬆️', color: '#ffe5f0' },
    { id: '22', bengali: 'খোলা', italian: 'Aprire', icon: '🔓', color: '#fff9e5' },
    { id: '23', bengali: 'বন্ধ করা', italian: 'Chiudere', icon: '🔒', color: '#f5f5ff' },
    { id: '24', bengali: 'কেনা', italian: 'Comprare', icon: '🛒', color: '#fff5e5' },
    { id: '25', bengali: 'বিক্রি করা', italian: 'Vendere', icon: '💰', color: '#e5f0ff' },
    { id: '26', bengali: 'দেওয়া', italian: 'Dare', icon: '🎁', color: '#ffe5f0' },
    { id: '27', bengali: 'নেওয়া', italian: 'Prendere', icon: '✋', color: '#fff9e5' },
    { id: '28', bengali: 'ধোয়া', italian: 'Lavare', icon: '🧼', color: '#e5fff5' }
  ];

  // Time (সময়) - Time related words
  const time: WordItem[] = [
    { id: '1', bengali: 'সকাল', italian: 'Mattina', icon: '🌅', color: '#fff0e5' },
    { id: '2', bengali: 'বিকাল', italian: 'Pomeriggio', icon: '☀️', color: '#fff9e5' },
    { id: '3', bengali: 'সন্ধ্যা', italian: 'Sera', icon: '🌆', color: '#fff0e5' },
    { id: '4', bengali: 'রাত', italian: 'Notte', icon: '🌙', color: '#e5e5f5' },
    { id: '5', bengali: 'দিন', italian: 'Giorno', icon: '☀️', color: '#fff9e5' },
    { id: '6', bengali: 'সোমবার', italian: 'Lunedì', icon: '📅', color: '#ffe5f0' },
    { id: '7', bengali: 'মঙ্গলবার', italian: 'Martedì', icon: '📅', color: '#e5f0ff' },
    { id: '8', bengali: 'বুধবার', italian: 'Mercoledì', icon: '📅', color: '#fff9e5' },
    { id: '9', bengali: 'বৃহস্পতিবার', italian: 'Giovedì', icon: '📅', color: '#f5ffe5' },
    { id: '10', bengali: 'শুক্রবার', italian: 'Venerdì', icon: '📅', color: '#ffe5f0' },
    { id: '11', bengali: 'শনিবার', italian: 'Sabato', icon: '📅', color: '#e5f0ff' },
    { id: '12', bengali: 'রবিবার', italian: 'Domenica', icon: '📅', color: '#fff9e5' },
    { id: '13', bengali: 'ঘণ্টা', italian: 'Ora', icon: '⏰', color: '#f5f5ff' },
    { id: '14', bengali: 'মিনিট', italian: 'Minuto', icon: '⏱️', color: '#fff5e5' },
    { id: '15', bengali: 'সেকেন্ড', italian: 'Secondo', icon: '⏱️', color: '#e5f5ff' },
    { id: '16', bengali: 'সপ্তাহ', italian: 'Settimana', icon: '📆', color: '#ffe5f0' },
    { id: '17', bengali: 'মাস', italian: 'Mese', icon: '📅', color: '#fff9e5' },
    { id: '18', bengali: 'বছর', italian: 'Anno', icon: '📆', color: '#e5fff5' },
    { id: '19', bengali: 'বসন্ত', italian: 'Primavera', icon: '🌸', color: '#fff5e5' },
    { id: '20', bengali: 'গ্রীষ্ম', italian: 'Estate', icon: '☀️', color: '#fff9e5' },
    { id: '21', bengali: 'শরৎ', italian: 'Autunno', icon: '🍂', color: '#ffe5f0' },
    { id: '22', bengali: 'শীত', italian: 'Inverno', icon: '❄️', color: '#e5f5ff' }
  ];

  // Places (স্থান) - Places and locations
  const places: WordItem[] = [
    { id: '1', bengali: 'বাড়ি', italian: 'Casa', icon: '🏠', color: '#fff9e5' },
    { id: '2', bengali: 'স্কুল', italian: 'Scuola', icon: '🏫', color: '#e5f0ff' },
    { id: '3', bengali: 'হাসপাতাল', italian: 'Ospedale', icon: '🏥', color: '#ffe5e5' },
    { id: '4', bengali: 'দোকান', italian: 'Negozio', icon: '🏪', color: '#fff9e5' },
    { id: '5', bengali: 'রেস্তোরাঁ', italian: 'Ristorante', icon: '🍽️', color: '#ffe5f0' },
    { id: '6', bengali: 'পার্ক', italian: 'Parco', icon: '🌳', color: '#f5ffe5' },
    { id: '7', bengali: 'সমুদ্র', italian: 'Mare', icon: '🌊', color: '#e5f5ff' },
    { id: '8', bengali: 'পাহাড়', italian: 'Montagna', icon: '⛰️', color: '#fff0e5' },
    { id: '9', bengali: 'শহর', italian: 'Città', icon: '🏙️', color: '#f0e5ff' },
    { id: '10', bengali: 'গ্রাম', italian: 'Villaggio', icon: '🏡', color: '#e5fff5' },
    { id: '11', bengali: 'বিমানবন্দর', italian: 'Aeroporto', icon: '✈️', color: '#fff5e5' },
    { id: '12', bengali: 'স্টেশন', italian: 'Stazione', icon: '🚉', color: '#e5f5ff' },
    { id: '13', bengali: 'লাইব্রেরি', italian: 'Biblioteca', icon: '📚', color: '#ffe5f0' },
    { id: '14', bengali: 'বাজার', italian: 'Mercato', icon: '🛒', color: '#fff9e5' },
    { id: '15', bengali: 'চার্চ', italian: 'Chiesa', icon: '⛪', color: '#f5f5ff' },
    { id: '16', bengali: 'থিয়েটার', italian: 'Teatro', icon: '🎭', color: '#fff5e5' },
    { id: '17', bengali: 'সিনেমা', italian: 'Cinema', icon: '🎬', color: '#e5f0ff' },
    { id: '18', bengali: 'হোটেল', italian: 'Albergo', icon: '🏨', color: '#ffe5f0' },
    { id: '19', bengali: 'ব্যাংক', italian: 'Banca', icon: '🏦', color: '#fff9e5' },
    { id: '20', bengali: 'ফার্মেসি', italian: 'Farmacia', icon: '💊', color: '#e5fff5' }
  ];

  // School (স্কুল) - School and education
  const school: WordItem[] = [
    { id: '1', bengali: 'শিক্ষক', italian: 'Insegnante', icon: '👨‍🏫', color: '#fff9e5' },
    { id: '2', bengali: 'ছাত্র', italian: 'Studente', icon: '👨‍🎓', color: '#e5f0ff' },
    { id: '3', bengali: 'ছাত্রী', italian: 'Studentessa', icon: '👩‍🎓', color: '#ffe5f0' },
    { id: '4', bengali: 'বই', italian: 'Libro', icon: '📖', color: '#f5ffe5' },
    { id: '5', bengali: 'পেন', italian: 'Penna', icon: '🖊️', color: '#fff0e5' },
    { id: '6', bengali: 'পেন্সিল', italian: 'Matita', icon: '✏️', color: '#f0e5ff' },
    { id: '7', bengali: 'খাতা', italian: 'Quaderno', icon: '📓', color: '#e5fff5' },
    { id: '8', bengali: 'বোর্ড', italian: 'Lavagna', icon: '📋', color: '#fff5e5' },
    { id: '9', bengali: 'পাঠ', italian: 'Lezione', icon: '📚', color: '#e5f5ff' },
    { id: '10', bengali: 'পরীক্ষা', italian: 'Esame', icon: '📝', color: '#ffe5f0' },
    { id: '11', bengali: 'হোমওয়ার্ক', italian: 'Compiti', icon: '📄', color: '#fff9e5' },
    { id: '12', bengali: 'ক্লাস', italian: 'Classe', icon: '🏫', color: '#f5f5ff' },
    { id: '13', bengali: 'ডেস্ক', italian: 'Banco', icon: '🗂️', color: '#fff5e5' },
    { id: '14', bengali: 'চেয়ার', italian: 'Sedia', icon: '🪑', color: '#e5f0ff' },
    { id: '15', bengali: 'ব্যাগ', italian: 'Zaino', icon: '🎒', color: '#ffe5f0' },
    { id: '16', bengali: 'ক্যালকুলেটর', italian: 'Calcolatrice', icon: '🔢', color: '#fff9e5' },
    { id: '17', bengali: 'রুলার', italian: 'Righello', icon: '📏', color: '#e5fff5' },
    { id: '18', bengali: 'ইরেজার', italian: 'Gomma', icon: '⬜️', color: '#fff5e5' },
    { id: '19', bengali: 'ক্যাম্পাস', italian: 'Campus', icon: '🏛️', color: '#e5f5ff' },
    { id: '20', bengali: 'বিশ্ববিদ্যালয়', italian: 'Università', icon: '🎓', color: '#ffe5f0' }
  ];

  // Emotions (আবেগ) - Emotions and feelings
  const emotions: WordItem[] = [
    { id: '1', bengali: 'খুশি', italian: 'Felice', icon: '😊', color: '#fff9e5' },
    { id: '2', bengali: 'দুঃখী', italian: 'Triste', icon: '😢', color: '#e5e5f5' },
    { id: '3', bengali: 'রাগান্বিত', italian: 'Arrabbiato', icon: '😠', color: '#ffe5e5' },
    { id: '4', bengali: 'ভয়', italian: 'Paura', icon: '😨', color: '#f5f5f5' },
    { id: '5', bengali: 'বিস্মিত', italian: 'Sorpreso', icon: '😲', color: '#fff9e5' },
    { id: '6', bengali: 'উত্তেজিত', italian: 'Eccitato', icon: '🤩', color: '#ffe5f0' },
    { id: '7', bengali: 'ক্লান্ত', italian: 'Stanco', icon: '😴', color: '#e5f0ff' },
    { id: '8', bengali: 'শান্ত', italian: 'Calmo', icon: '😌', color: '#f5ffe5' },
    { id: '9', bengali: 'গর্বিত', italian: 'Orgoglioso', icon: '😎', color: '#fff9e5' },
    { id: '10', bengali: 'লজ্জিত', italian: 'Imbarazzato', icon: '😳', color: '#ffe5f0' },
    { id: '11', bengali: 'আতঙ্কিত', italian: 'Preoccupato', icon: '😟', color: '#e5f0ff' },
    { id: '12', bengali: 'উদ্দীপিত', italian: 'Emozionato', icon: '🎉', color: '#fff9e5' },
    { id: '13', bengali: 'আনন্দিত', italian: 'Contento', icon: '😃', color: '#f5ffe5' },
    { id: '14', bengali: 'নিরাশ', italian: 'Deluso', icon: '😞', color: '#e5e5f5' },
    { id: '15', bengali: 'প্রেম', italian: 'Amore', icon: '❤️', color: '#ffe5f0' },
    { id: '16', bengali: 'ঘৃণা', italian: 'Odio', icon: '💔', color: '#ffe5e5' },
    { id: '17', bengali: 'আনন্দ', italian: 'Gioia', icon: '😄', color: '#fff9e5' },
    { id: '18', bengali: 'আশা', italian: 'Speranza', icon: '✨', color: '#e5f0ff' }
  ];

  // Sports (খেলা) - Sports and activities
  const sports: WordItem[] = [
    { id: '1', bengali: 'ফুটবল', italian: 'Calcio', icon: '⚽', color: '#fff9e5' },
    { id: '2', bengali: 'বাস্কেটবল', italian: 'Pallacanestro', icon: '🏀', color: '#ffe5f0' },
    { id: '3', bengali: 'টেনিস', italian: 'Tennis', icon: '🎾', color: '#f5ffe5' },
    { id: '4', bengali: 'ভলিবল', italian: 'Pallavolo', icon: '🏐', color: '#fff9e5' },
    { id: '5', bengali: 'সাঁতার', italian: 'Nuoto', icon: '🏊', color: '#e5f5ff' },
    { id: '6', bengali: 'দৌড়', italian: 'Corsa', icon: '🏃', color: '#ffe5f0' },
    { id: '7', bengali: 'সাইকেল চালানো', italian: 'Ciclismo', icon: '🚴', color: '#fff9e5' },
    { id: '8', bengali: 'স্কেটিং', italian: 'Pattinaggio', icon: '⛸️', color: '#e5fff5' },
    { id: '9', bengali: 'স্কিইং', italian: 'Sci', icon: '⛷️', color: '#fff5e5' },
    { id: '10', bengali: 'বক্সিং', italian: 'Boxe', icon: '🥊', color: '#e5f5ff' },
    { id: '11', bengali: 'যুগল', italian: 'Yoga', icon: '🧘', color: '#ffe5f0' },
    { id: '12', bengali: 'জিমন্যাস্টিক', italian: 'Ginnastica', icon: '🤸', color: '#fff9e5' },
    { id: '13', bengali: 'কিকবক্সিং', italian: 'Kickboxing', icon: '🥋', color: '#f5f5ff' },
    { id: '14', bengali: 'সাইকেল', italian: 'Bicicletta', icon: '🚲', color: '#fff5e5' },
    { id: '15', bengali: 'বল', italian: 'Palla', icon: '⚽', color: '#e5f0ff' },
    { id: '16', bengali: 'প্রশিক্ষক', italian: 'Allenatore', icon: '👨‍🏫', color: '#ffe5f0' },
    { id: '17', bengali: 'খেলোয়াড়', italian: 'Giocatore', icon: '👤', color: '#fff9e5' },
    { id: '18', bengali: 'প্রতিযোগিতা', italian: 'Competizione', icon: '🏆', color: '#e5fff5' }
  ];

  // Suffix -azione words
  const suffixAzione: WordItem[] = [
    { id: '1', bengali: 'তথ্য', italian: 'Informazione', icon: '📚', color: '#fff9e5' },
    { id: '2', bengali: 'যোগাযোগ', italian: 'Comunicazione', icon: '📚', color: '#ffe5f0' },
    { id: '3', bengali: 'শিক্ষা', italian: 'Educazione', icon: '📚', color: '#f5ffe5' },
    { id: '4', bengali: 'পরিস্থিতি', italian: 'Situazione', icon: '📚', color: '#fff9e5' },
    { id: '5', bengali: 'সংগঠন', italian: 'Organizzazione', icon: '📚', color: '#e5f5ff' },
    { id: '6', bengali: 'জাতি', italian: 'Nazione', icon: '📚', color: '#ffe5f0' },
    { id: '7', bengali: 'সম্পর্ক', italian: 'Relazione', icon: '📚', color: '#fff9e5' },
    { id: '8', bengali: 'স্টেশন', italian: 'Stazione', icon: '📚', color: '#e5fff5' },
    { id: '9', bengali: 'প্রস্তুতি', italian: 'Preparazione', icon: '📚', color: '#fff5e5' },
    { id: '10', bengali: 'অবস্থান', italian: 'Posizione', icon: '📚', color: '#e5f0ff' },
    { id: '11', bengali: 'অভিব্যক্তি', italian: 'Espressione', icon: '📚', color: '#ffe5e5' },
    { id: '12', bengali: 'অনুমোদন', italian: 'Autorizzazione', icon: '📚', color: '#f5f5ff' }
  ];

  // Suffix -zione words
  const suffixZione: WordItem[] = [
    { id: '1', bengali: 'উৎপাদন', italian: 'Produzione', icon: '📝', color: '#fff9e5' },
    { id: '2', bengali: 'নির্মাণ', italian: 'Costruzione', icon: '📝', color: '#ffe5f0' },
    { id: '3', bengali: 'শিক্ষা', italian: 'Istruzione', icon: '📝', color: '#f5ffe5' },
    { id: '4', bengali: 'দিকনির্দেশনা', italian: 'Direzione', icon: '📝', color: '#fff9e5' },
    { id: '5', bengali: 'সুরক্ষা', italian: 'Protezione', icon: '📝', color: '#e5f5ff' },
    { id: '6', bengali: 'সংস্করণ', italian: 'Edizione', icon: '📝', color: '#fff5e5' },
    { id: '7', bengali: 'অভিযান', italian: 'Spedizione', icon: '📝', color: '#e5f0ff' },
    { id: '8', bengali: 'সংজ্ঞা', italian: 'Definizione', icon: '📝', color: '#ffe5e5' }
  ];

  // Suffix -mente words
  const suffixMente: WordItem[] = [
    { id: '1', bengali: 'দ্রুত', italian: 'Velocemente', icon: '⚡', color: '#fff9e5' },
    { id: '2', bengali: 'ধীরে', italian: 'Lentamente', icon: '⚡', color: '#ffe5f0' },
    { id: '3', bengali: 'সহজে', italian: 'Facilmente', icon: '⚡', color: '#f5ffe5' },
    { id: '4', bengali: 'স্পষ্টভাবে', italian: 'Chiaramente', icon: '⚡', color: '#fff9e5' },
    { id: '5', bengali: 'সাধারণত', italian: 'Normalmente', icon: '⚡', color: '#e5f5ff' },
    { id: '6', bengali: 'সাবধানে', italian: 'Attentamente', icon: '⚡', color: '#fff5e5' },
    { id: '7', bengali: 'সুন্দরভাবে', italian: 'Bellamente', icon: '⚡', color: '#e5f0ff' },
    { id: '8', bengali: 'সঠিকভাবে', italian: 'Correttamente', icon: '⚡', color: '#ffe5e5' },
    { id: '9', bengali: 'দ্রুত', italian: 'Rapidamente', icon: '⚡', color: '#f5f5ff' }
  ];

  // Suffix -ità words
  const suffixIta: WordItem[] = [
    { id: '1', bengali: 'শহর', italian: 'Città', icon: '🏛️', color: '#fff9e5' },
    { id: '2', bengali: 'বিশ্ববিদ্যালয়', italian: 'Università', icon: '🏛️', color: '#ffe5f0' },
    { id: '3', bengali: 'গুণমান', italian: 'Qualità', icon: '🏛️', color: '#f5ffe5' },
    { id: '4', bengali: 'স্বাধীনতা', italian: 'Libertà', icon: '🏛️', color: '#fff9e5' },
    { id: '5', bengali: 'কার্যকলাপ', italian: 'Attività', icon: '🏛️', color: '#e5f5ff' },
    { id: '6', bengali: 'সম্ভাবনা', italian: 'Possibilità', icon: '🏛️', color: '#fff5e5' },
    { id: '7', bengali: 'ক্ষমতা', italian: 'Autorità', icon: '🏛️', color: '#e5f0ff' },
    { id: '8', bengali: 'পরিমাণ', italian: 'Quantità', icon: '🏛️', color: '#ffe5e5' }
  ];

  // Suffix -ore/-atore words (Jobs)
  const suffixOreAtore: WordItem[] = [
    { id: '1', bengali: 'ডাক্তার', italian: 'Dottore', icon: '💼', color: '#fff9e5' },
    { id: '2', bengali: 'শিক্ষক', italian: 'Insegnante', icon: '💼', color: '#ffe5f0' },
    { id: '3', bengali: 'শ্রমিক', italian: 'Lavoratore', icon: '💼', color: '#f5ffe5' },
    { id: '4', bengali: 'চালক', italian: 'Guidatore', icon: '💼', color: '#fff9e5' },
    { id: '5', bengali: 'অভিনেতা', italian: 'Attore', icon: '💼', color: '#e5f5ff' },
    { id: '6', bengali: 'লেখক', italian: 'Scrittore', icon: '💼', color: '#fff5e5' },
    { id: '7', bengali: 'পাঠক', italian: 'Lettore', icon: '💼', color: '#e5f0ff' },
    { id: '8', bengali: 'কৃষক', italian: 'Agricoltore', icon: '💼', color: '#ffe5e5' },
    { id: '9', bengali: 'বিক্রেতা', italian: 'Venditore', icon: '💼', color: '#f5f5ff' }
  ];

  // Suffix -oso words
  const suffixOso: WordItem[] = [
    { id: '1', bengali: 'বিপজ্জনক', italian: 'Pericoloso', icon: '✨', color: '#fff9e5' },
    { id: '2', bengali: 'বিখ্যাত', italian: 'Famoso', icon: '✨', color: '#ffe5f0' },
    { id: '3', bengali: 'কৌতূহলী', italian: 'Curioso', icon: '✨', color: '#f5ffe5' },
    { id: '4', bengali: 'শব্দযুক্ত', italian: 'Rumoroso', icon: '✨', color: '#fff9e5' },
    { id: '5', bengali: 'ঈর্ষান্বিত', italian: 'Geloso', icon: '✨', color: '#e5f5ff' },
    { id: '6', bengali: 'আনন্দদায়ক', italian: 'Gioioso', icon: '✨', color: '#fff5e5' },
    { id: '7', bengali: 'ব্যস্ত', italian: 'Affannoso', icon: '✨', color: '#e5f0ff' },
    { id: '8', bengali: 'সুন্দর', italian: 'Meraviglioso', icon: '✨', color: '#ffe5e5' }
  ];

  // Suffix -issimo words
  const suffixIssimo: WordItem[] = [
    { id: '1', bengali: 'খুব সুন্দর', italian: 'Bellissimo', icon: '⭐', color: '#fff9e5' },
    { id: '2', bengali: 'খুব সুস্বাদু', italian: 'Buonissimo', icon: '⭐', color: '#ffe5f0' },
    { id: '3', bengali: 'খুব শক্তিশালী', italian: 'Fortissimo', icon: '⭐', color: '#f5ffe5' },
    { id: '4', bengali: 'খুব প্রিয়', italian: 'Carissimo', icon: '⭐', color: '#fff9e5' },
    { id: '5', bengali: 'খুব ভালো', italian: 'Ottimo', icon: '⭐', color: '#e5f5ff' },
    { id: '6', bengali: 'খুব বড়', italian: 'Grandissimo', icon: '⭐', color: '#fff5e5' },
    { id: '7', bengali: 'খুব ছোট', italian: 'Piccolissimo', icon: '⭐', color: '#e5f0ff' }
  ];

  // Verbs infinitive
  const verbsInfinitive: WordItem[] = [
    { id: '1', bengali: 'কথা বলা', italian: 'Parlare', icon: '📖', color: '#fff9e5' },
    { id: '2', bengali: 'খাওয়া', italian: 'Mangiare', icon: '📖', color: '#ffe5f0' },
    { id: '3', bengali: 'পড়াশোনা করা', italian: 'Studiare', icon: '📖', color: '#f5ffe5' },
    { id: '4', bengali: 'পড়া', italian: 'Leggere', icon: '📖', color: '#fff9e5' },
    { id: '5', bengali: 'লেখা', italian: 'Scrivere', icon: '📖', color: '#e5f5ff' },
    { id: '6', bengali: 'ঘুমানো', italian: 'Dormire', icon: '📖', color: '#ffe5f0' },
    { id: '7', bengali: 'রওনা হওয়া', italian: 'Partire', icon: '📖', color: '#fff9e5' },
    { id: '8', bengali: 'যাওয়া', italian: 'Andare', icon: '📖', color: '#fff5e5' },
    { id: '9', bengali: 'আসা', italian: 'Venire', icon: '📖', color: '#e5f0ff' },
    { id: '10', bengali: 'করা', italian: 'Fare', icon: '📖', color: '#ffe5e5' },
    { id: '11', bengali: 'দেখা', italian: 'Vedere', icon: '📖', color: '#f5f5ff' },
    { id: '12', bengali: 'শোনা', italian: 'Sentire', icon: '📖', color: '#fff5e5' }
  ];

  // Prefix ri- words
  const prefixRi: WordItem[] = [
    { id: '1', bengali: 'ফিরে আসা', italian: 'Ritornare', icon: '🔄', color: '#fff9e5' },
    { id: '2', bengali: 'পুনরাবৃত্তি করা', italian: 'Ripetere', icon: '🔄', color: '#ffe5f0' },
    { id: '3', bengali: 'আবার করা', italian: 'Rifare', icon: '🔄', color: '#f5ffe5' },
    { id: '4', bengali: 'নতুন করে শুরু করা', italian: 'Ricominciare', icon: '🔄', color: '#fff9e5' },
    { id: '5', bengali: 'পুনরায় দেখা', italian: 'Rivedere', icon: '🔄', color: '#e5f5ff' },
    { id: '6', bengali: 'পুনরায় চিন্তা করা', italian: 'Ripensare', icon: '🔄', color: '#fff5e5' },
    { id: '7', bengali: 'পুনরায় খোলা', italian: 'Riaprire', icon: '🔄', color: '#e5f0ff' }
  ];


  const getTitle = () => {
    if (fetchedCategoryTitle) {
      return currentLanguage === 'it' ? fetchedCategoryTitle.it : currentLanguage === 'bn' ? fetchedCategoryTitle.bn : fetchedCategoryTitle.en;
    }
    switch(type) {
      case 'abc': return content[currentLanguage].abc;
      case 'numbers': return content[currentLanguage].numbers;
      case 'fruits': return content[currentLanguage].fruits;
      case 'animals': return content[currentLanguage].animals;
      case 'colors': return content[currentLanguage].colors;
      case 'body': return content[currentLanguage].body;
      case 'food': return content[currentLanguage].food;
      case 'family': return content[currentLanguage].family;
      case 'clothes': return content[currentLanguage].clothes;
      case 'vehicles': return content[currentLanguage].vehicles;
      case 'weather': return content[currentLanguage].weather;
      case 'greetings': return content[currentLanguage].greetings;
      case 'commonWords': return content[currentLanguage].commonWords;
      case 'advancedWords': return content[currentLanguage].advancedWords;
      case 'actions': return content[currentLanguage].actions;
      case 'time': return content[currentLanguage].time;
      case 'places': return content[currentLanguage].places;
      case 'school': return content[currentLanguage].school;
      case 'emotions': return content[currentLanguage].emotions;
      case 'sports': return content[currentLanguage].sports;
      case 'suffix_azione': return content[currentLanguage].suffixAzione;
      case 'suffix_zione': return content[currentLanguage].suffixZione;
      case 'suffix_mente': return content[currentLanguage].suffixMente;
      case 'suffix_ita': return content[currentLanguage].suffixIta;
      case 'suffix_ore_atore': return content[currentLanguage].suffixOreAtore;
      case 'suffix_oso': return content[currentLanguage].suffixOso;
      case 'suffix_issimo': return content[currentLanguage].suffixIssimo;
      case 'verbs_infinitive': return content[currentLanguage].verbsInfinitive;
      case 'prefix_ri': return content[currentLanguage].prefixRi;
      default: return '';
    }
  };

  const getItems = (): string[] | WordItem[] | { letter: string; color: string }[] | { number: string; color: string }[] => {
    if (type === 'abc') return alphabetLetters;
    if (type === 'numbers') return numbers;
    if (fetchedItems && fetchedItems.length > 0) return fetchedItems;
    switch(type) {
      case 'fruits': return fruits;
      case 'animals': return animals;
      case 'colors': return colors;
      case 'body': return body;
      case 'food': return food;
      case 'family': return family;
      case 'clothes': return clothes;
      case 'vehicles': return vehicles;
      case 'weather': return weather;
      case 'greetings': return greetings;
      case 'commonWords': return commonWords;
      case 'advancedWords': return advancedWords;
      case 'actions': return actions;
      case 'time': return time;
      case 'places': return places;
      case 'school': return school;
      case 'emotions': return emotions;
      case 'sports': return sports;
      case 'suffix_azione': return suffixAzione;
      case 'suffix_zione': return suffixZione;
      case 'suffix_mente': return suffixMente;
      case 'suffix_ita': return suffixIta;
      case 'suffix_ore_atore': return suffixOreAtore;
      case 'suffix_oso': return suffixOso;
      case 'suffix_issimo': return suffixIssimo;
      case 'verbs_infinitive': return verbsInfinitive;
      case 'prefix_ri': return prefixRi;
      default: return [];
    }
  };

  const items = getItems();
  const title = getTitle();
  const isBilingual = type !== 'abc' && type !== 'numbers';
  const isLettersOrNumbers = type === 'abc' || type === 'numbers';

  // Speak category name when screen loads (only once per navigation)
  useEffect(() => {
    if (!type) return;
    
    // Check if we've already spoken for this type
    const lastSpokenType = sessionStorage.getItem('lastSpokenCategoryType');
    const lastSpokenTime = sessionStorage.getItem('lastSpokenCategoryTime');
    const now = Date.now();
    
    // Only speak if it's a different type or more than 1 second has passed
    if (lastSpokenType === type && lastSpokenTime && (now - parseInt(lastSpokenTime)) < 1000) {
      return;
    }
    
    const speakCategoryName = async () => {
      try {
        const categoryNames: { [key: string]: string } = {
          'abc': 'Alfabeto',
          'numbers': 'Numeri',
          'fruits': 'Frutta',
          'animals': 'Animali',
          'colors': 'Colori',
          'body': 'Parti del Corpo',
          'food': 'Cibo',
          'family': 'Famiglia',
          'clothes': 'Vestiti',
          'vehicles': 'Veicoli',
          'weather': 'Tempo',
          'greetings': 'Saluti'
        };
        
        const italianName = categoryNames[type] || '';
        if (italianName) {
          // Mark as spoken
          sessionStorage.setItem('lastSpokenCategoryType', type);
          sessionStorage.setItem('lastSpokenCategoryTime', now.toString());
          
          // Small delay to ensure audio is ready
          await new Promise(resolve => setTimeout(resolve, 500));
          await speakText(italianName, 'answer');
        }
      } catch (error) {
        console.error('Error speaking category:', error);
      }
    };

    speakCategoryName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Italian number names (0-100)
  const getItalianNumberName = (num: string): string => {
    const n = parseInt(num);
    const italianNumbers: { [key: number]: string } = {
      0: 'zero', 1: 'uno', 2: 'due', 3: 'tre', 4: 'quattro',
      5: 'cinque', 6: 'sei', 7: 'sette', 8: 'otto', 9: 'nove',
      10: 'dieci', 11: 'undici', 12: 'dodici', 13: 'tredici',
      14: 'quattordici', 15: 'quindici', 16: 'sedici',
      17: 'diciassette', 18: 'diciotto', 19: 'diciannove',
      20: 'venti', 30: 'trenta', 40: 'quaranta', 50: 'cinquanta',
      60: 'sessanta', 70: 'settanta', 80: 'ottanta', 90: 'novanta',
      100: 'cento'
    };

    if (italianNumbers[n]) {
      return italianNumbers[n];
    }

    // Handle compound numbers (21-99)
    if (n > 20 && n < 100) {
      const tens = Math.floor(n / 10) * 10;
      const ones = n % 10;
      let name = italianNumbers[tens] || '';
      
      if (ones > 0) {
        // Special cases: numbers ending in 1 or 8
        if (ones === 1 || ones === 8) {
          name = name.slice(0, -1); // Remove last vowel
        }
        name += italianNumbers[ones];
      }
      
      return name;
    }

    return num; // Fallback to number
  };

  const handleItemClick = async (item: string | WordItem | { letter: string; color: string } | { number: string; color: string }) => {
    let itemId: string;
    let textToSpeak: string;
    
    if (type === 'abc') {
      const letterItem = item as { letter: string; color: string };
      itemId = letterItem.letter;
      textToSpeak = letterItem.letter;
    } else if (type === 'numbers') {
      const numberItem = item as { number: string; color: string };
      itemId = numberItem.number;
      textToSpeak = getItalianNumberName(numberItem.number);
    } else {
      const wordItem = item as WordItem;
      itemId = wordItem.id;
      textToSpeak = wordItem.italian;
    }
    
    setSelectedItem(itemId);
    
    // Speak the word in Italian
    try {
      await speakText(textToSpeak, 'answer');
    } catch (error) {
      console.error('Error speaking:', error);
    }

    // Remove selection after a short delay
    setTimeout(() => {
      setSelectedItem(null);
    }, 500);
  };

  return (
    <div className="words-detail-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{title}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* Grid of letters/numbers/fruits/animals */}
      <div className="words-container">
        <div className="words-grid">
        {isBilingual ? (items as WordItem[]).map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className={`word-item ${selectedItem === item.id ? 'selected' : ''}`}
            onClick={() => handleItemClick(item)}
            style={{ backgroundColor: item.color }}
          >
            <div className="word-bilingual">
              <span className="word-icon">{item.icon}</span>
              <span className="word-text-italian">{item.italian}</span>
              <span className="word-text-bengali">{item.bengali}</span>
            </div>
          </div>
        )) : isLettersOrNumbers ? type === 'abc' ? (items as { letter: string; color: string }[]).map((item, index) => (
          <div
            key={`${item.letter}-${index}`}
            className={`word-item ${selectedItem === item.letter ? 'selected' : ''}`}
            onClick={() => handleItemClick(item)}
            style={{ backgroundColor: item.color }}
          >
            <span className="word-text">{item.letter}</span>
          </div>
        )) : (items as { number: string; color: string }[]).map((item, index) => (
          <div
            key={`${item.number}-${index}`}
            className={`word-item ${selectedItem === item.number ? 'selected' : ''}`}
            onClick={() => handleItemClick(item)}
            style={{ backgroundColor: item.color }}
          >
            <span className="word-text">{item.number}</span>
          </div>
        )) : (items as string[]).map((item, index) => (
          <div
            key={`${item}-${index}`}
            className={`word-item ${selectedItem === item ? 'selected' : ''}`}
            onClick={() => handleItemClick(item)}
          >
            <span className="word-text">{item}</span>
          </div>
        ))}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default WordsDetailScreen;

